<template>
  <div>
    <b-breadcrumb class="breadcrumb-slash mb-1">
      <b-breadcrumb-item :to="{ name: 'dashboard-1' }">
        {{ $t("HomePage.HomePage") }}
        <!-- Dashboard -->
      </b-breadcrumb-item>
      <b-breadcrumb-item active>
        {{ $t("OrderListView.OrderList") }}
        <!-- รายการคำสั่งซื้อ -->
      </b-breadcrumb-item>
    </b-breadcrumb>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="3"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>
              {{ $t("OrderListView.Show") }}
              <!-- Show -->
            </label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>
              {{ $t("OrderListView.Entries") }}
              <!-- entries -->
            </label>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <div class="d-flex align-items-center justify-content-end">
              <export-excel
                class="btn"
                :data="json_data"
                :fields="json_fields"
                worksheet="My Worksheet"
                name="filename.xls"
              >
                <b-button variant="primary">
                  <span class="text-nowrap">Export</span>
                </b-button>
              </export-excel>
            </div>
          </b-col>

          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end mt-1">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('OrderListView.Search')"
              />
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refOrderListTable"
        class="position-relative"
        :items="fetchOrders"
        responsive
        :fields="tableColumns_i18n"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('OrderListView.NoMatching')"
        :sort-desc.sync="isSortDirDesc"
      >

        <template #cell(index)="data">
          <div style="text-align: center;">
            {{ (currentPage - 1) * perPage + data.index + 1 }}
            </div>
        </template>

        <template #cell(no)="data">
          <b-link
            :to="{ name: 'order-view', params: { id: data.item.id } }"
            class="font-weight-bold d-block text-nowrap"
          >
            {{ data.item.no }}
          </b-link>
        </template>

        <template #cell(orderTotal)="data">
          <span>{{ data.item.orderTotal.toLocaleString("en-US") }} บาท</span>
        </template>

        <template #cell(orderStatus)="data">
          <b-badge
            pill
            :variant="`light-${resolveStatusVariant(data.item.orderStatus)}`"
            class="text-capitalize"
          >
            {{ data.item.orderStatus }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item>
              <feather-icon icon="DownloadIcon" />
              <span class="align-middle ml-50">
                {{ $t("OrderListView.Download") }}
                <!-- Download -->
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              :to="{ name: 'order-view', params: { id: data.item.id } }"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">
                {{ $t("OrderListView.Edit") }}
                <!-- Edit -->
              </span>
            </b-dropdown-item>

            <b-dropdown-item>
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">
                {{ $t("OrderListView.Delete") }}
                <!-- Delete -->
              </span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              {{ $t("OrderListView.Showing") }}
              <!-- Showing -->
              {{ dataMeta.from }}
              {{ $t("OrderListView.to") }}
              <!-- to  -->
              {{ dataMeta.to }}
              {{ $t("OrderListView.of") }}
              <!-- of -->
              {{ dataMeta.of }}
              {{ $t("OrderListView.Entries") }}
              <!-- entries -->
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalOrders"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BBreadcrumb,
  BBreadcrumbItem,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import { ref, onUnmounted } from '@vue/composition-api'
// eslint-disable-next-line import/no-cycle
import { avatarText } from '@core/utils/filter'
// import ProductListFilters from './ProductListFilters.vue'
import axios, { baseURL } from '@axios'
import useOrderList from './useOrderList'
import orderStoreModule from './orderStoreModule'
import { getUserData } from '@/auth/utils'

export default {
  components: {
    // ProductListFilters,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BBreadcrumb,
    BBreadcrumbItem,

    vSelect,
  },
  data() {
    return {
      json_fields: {
        'ORDER NUMBER': 'no',
        'ORDER DATE': 'createdOnString',
        'USER NAME': 'customerName',

        'PRODUCT PRICE TOTAL': {
          field: 'orderTotal',
          callback: value => `${value}`,
        },
        'ORDER STATUS': 'orderStatusString',
      },
      json_data: [
        {
          name: 'Tony Peña',
          city: 'New York',
          country: 'United States',
          birthdate: '1978-03-15',
          phone: {
            mobile: '1-541-754-3010',
            landline: '(541) 754-3010',
          },
        },
        {
          name: 'Thessaloniki',
          city: 'Athens',
          country: 'Greece',
          birthdate: '1987-11-23',
          phone: {
            mobile: '+1 855 275 5071',
            landline: '(2741) 2621-244',
          },
        },
      ],
      json_meta: [
        [
          {
            key: 'charset',
            value: 'utf-8',
          },
        ],
      ],
    }
  },

  // computed: {
  //   tableColumns_i18n() {
  //     return [];
  //   }
  // }

  computed: {
    tableColumns_i18n() {
      return [
        { key: 'index',
        label: this.$t('OrderListView.Number')
        },
        {
          key: 'no',
          label: this.$t('OrderListView.OrderNumber'),
          sortable: true,
        },
        //
        {
          key: 'jobID',
          label: this.$t('OrderListView.BookingNumber'),
          sortable: true,
        },
        {
          key: 'createdOnString',
          label: this.$t('OrderListView.OrderDate'),
          sortable: true,
        },
        {
          key: 'customerName',
          label: this.$t('OrderListView.NameSurname'),
          sortable: true,
        },
        { key: 'customerPhone',
          label: this.$t('OrderListView.Phone'), 
          sortable: true 
        },
        {
          key: 'orderTotal',
          label: this.$t('OrderListView.TotalPrice'),
          sortable: true,
        },
        {
          key: 'orderStatusString',
          label: this.$t('OrderListView.OrderStatus'),
          sortable: true,
        },
        { key: 'actions' },
      ]
    },
  },
  mounted() {
    const userData = getUserData()

    const body = {
      pagination: {
        total: 0,
        current: 1,
        pageSize: 20000,
      },
      search: {
        customerId: null,
        shopId: userData.shopId,
        orderStatus: [

        ],
        shippingStatus: [

        ],
        createdOnStart: null,
        createdOnEnd: null,
        productName: null,
        sku: '',
      },
      sort: {
        predicate: 'created_date',
        reverse: true,
      },
    }

    axios
      .post(`${baseURL}/api/SaleOrder/GetOrderList`, body, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${userData.token}`,
        },
      })
      .then(response => {
        // const queryLowered = q.toLowerCase()
        console.log("response", response);
        this.json_data = response.data.data.list
        console.log("this.json_data", this.json_data);
      })
      .catch(error => console.log(error))

    /*
    store
      .dispatch("order/fetchOrders", {
    "pagination": {
        "total": 0,
        "current": 1,
        "pageSize": 20000
    },
    "search": {
        "customerId": null,
        "shopId": "f3f1f46e-1401-4506-4d6e-08db1f2d4713",
        "orderStatus": [],
        "shippingStatus": [],
        "createdOnStart": null,
        "createdOnEnd": null,
        "productName": "",
        "sku": ""
    },
    "sort": {
        "predicate": "",
        "reverse": true
    }
})
      .then((response) => {
        const { orders, total } = response;
        this.json_data = orders;
        console.log("this.json_data", this.json_data);
      })
      .catch(() => {});
      */
  },
  setup() {
    // Register module
    if (!store.hasModule('order')) {
      store.registerModule('order', orderStoreModule)
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule('order')) store.unregisterModule('order')
    })

    const statusOptions = [
      { label: 'Pending', value: 'Pending' },
      { label: 'Active', value: 'Active' },
      { label: 'Inactive', value: 'Inactive' },
    ]

    const {
      fetchOrders,
      tableColumns,
      perPage,
      currentPage,
      totalOrders,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refOrderListTable,
      refetchData,

      // UI
      resolveStatusVariant,

      // Extra Filters
      // productNameFilter,
      // productCategoryFilter,
      statusFilter,
    } = useOrderList()

    return {
      fetchOrders,
      tableColumns,
      perPage,
      currentPage,
      totalOrders,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refOrderListTable,
      refetchData,

      // Filter
      avatarText,

      // UI
      resolveStatusVariant,

      statusOptions,

      // Extra Filters
      // productNameFilter,
      // productCategoryFilter,
      statusFilter,
    }
  },

  //   const tableColumns = [
  //   { key: "no", label: "ORDER NUMBER", sortable: true },
  //   { key: "createdOnString", label: "ORDER DATE", sortable: true },
  //   { key: "customerName", label: "USER NAME", sortable: true },
  //   // { key: 'customerPhone', label: 'USER MOBILE', sortable: true },
  //   { key: "orderTotal", label: "PRODUCT PRICE TOTAL", sortable: true },
  //   { key: "orderStatusString", label: "ORDER STATUS", sortable: true },
  //   { key: "actions" },
  // ];
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
